import React from "react";
import { Row, Col } from "react-bootstrap";
import { Icon } from "@iconify/react";
import styles from './AccessosWiki.module.scss';

/**
 * 🔌 Wiki - O que é a API
 * 
 * Documentação introdutória sobre a API da plataforma.
 * Explica o que é uma API, para que serve e como começar.
 * Voltada para produtores leigos (não técnica).
 */
const OQueEApiWiki = () => {
  return (
    <div className={styles.wikiAccessos}>
      {/* Hero Section */}
      <section className={styles.heroSection}>
        <div className={styles.heroBadge}>
          <Icon icon="mdi:api" />
          <span>API da Pagmus</span>
        </div>
        <p className={styles.heroText}>
          Conecte sua plataforma com a Pagmus de forma automática. A API permite que sistemas 
          externos criem produtos, gerenciem vendas e muito mais, sem precisar acessar o painel manualmente.
        </p>
      </section>

      {/* O que é uma API */}
      <section className={styles.section}>
        <div className={styles.sectionHeader}>
          <span className={styles.sectionTitle}>O que é uma API?</span>
          <p className={styles.sectionSubtitle}>
            Entenda de forma simples o que é e para que serve
          </p>
        </div>

        <div className={styles.infoCard}>
          <Icon icon="mdi:information" className={styles.infoIcon} />
          <div>
            <p>
              <strong>API</strong> significa "Interface de Programação de Aplicações" (Application Programming Interface). 
              Em termos simples, é uma forma de <strong>conectar seu sistema com a Pagmus</strong> de forma automática.
            </p>
            <p style={{ marginTop: '12px', marginBottom: 0 }}>
              Imagine que você tem um sistema próprio (site, aplicativo, ou ferramenta) e quer que ele 
              <strong>crie produtos automaticamente</strong> na Pagmus, ou <strong>registre vendas</strong> sem você 
              precisar fazer isso manualmente. A API permite isso!
            </p>
          </div>
        </div>
      </section>

      {/* Para que serve */}
      <section className={styles.section}>
        <div className={styles.sectionHeader}>
          <span className={styles.sectionTitle}>Para que serve a API?</span>
          <p className={styles.sectionSubtitle}>
            Principais usos e benefícios
          </p>
        </div>

        <Row className="g-4">
          <Col lg={6}>
            <div className={styles.participantCard}>
              <div className={`${styles.cardIcon} ${styles.iconSuccess}`}>
                <Icon icon="mdi:robot" />
              </div>
              <div className={styles.cardContent}>
                <div className={`${styles.cardBadge} ${styles.badgeSuccess}`}>Automação</div>
                <span className={styles.cardTitle}>Automatizar Tarefas</span>
                <p>
                  Crie produtos, registre vendas e gerencie informações <strong>automaticamente</strong> 
                  sem precisar acessar o painel manualmente. Economize tempo e evite erros!
                </p>
              </div>
            </div>
          </Col>

          <Col lg={6}>
            <div className={styles.participantCard}>
              <div className={`${styles.cardIcon} ${styles.iconPlatform}`}>
                <Icon icon="mdi:link-variant" />
              </div>
              <div className={styles.cardContent}>
                <div className={`${styles.cardBadge} ${styles.badgePlatform}`}>Integração</div>
                <span className={styles.cardTitle}>Integrar Sistemas</span>
                <p>
                  Conecte sua <strong>loja online</strong>, <strong>sistema de gestão</strong> ou 
                  <strong>aplicativo</strong> com a Pagmus. Tudo sincronizado em tempo real!
                </p>
              </div>
            </div>
          </Col>

          <Col lg={6}>
            <div className={styles.participantCard}>
              <div className={`${styles.cardIcon} ${styles.iconAffiliate}`}>
                <Icon icon="mdi:sync" />
              </div>
              <div className={styles.cardContent}>
                <div className={`${styles.cardBadge} ${styles.badgeAffiliate}`}>Sincronização</div>
                <span className={styles.cardTitle}>Sincronizar Dados</span>
                <p>
                  Mantenha seus produtos e vendas <strong>sempre atualizados</strong> entre diferentes 
                  sistemas. Uma mudança em um lugar reflete automaticamente em outro.
                </p>
              </div>
            </div>
          </Col>

          <Col lg={6}>
            <div className={styles.participantCard}>
              <div className={`${styles.cardIcon} ${styles.iconWarning}`}>
                <Icon icon="mdi:chart-line" />
              </div>
              <div className={styles.cardContent}>
                <div className={`${styles.cardBadge} ${styles.badgeWarning}`}>Escalabilidade</div>
                <span className={styles.cardTitle}>Crescer Mais Rápido</span>
                <p>
                  Processe <strong>centenas ou milhares</strong> de produtos e vendas de forma 
                  automática. A API suporta grandes volumes sem problemas!
                </p>
              </div>
            </div>
          </Col>
        </Row>
      </section>

      {/* Como Funciona */}
      <section className={styles.section}>
        <div className={styles.sectionHeader}>
          <span className={styles.sectionTitle}>Como Funciona?</span>
          <p className={styles.sectionSubtitle}>
            Passo a passo simplificado
          </p>
        </div>

        <Row className="g-4">
          <Col lg={6}>
            <div className={styles.configCard}>
              <div className={styles.configNumber}>1</div>
              <div className={styles.configIcon}>
                <Icon icon="mdi:key-variant" />
              </div>
              <div className={styles.configContent}>
                <span className={styles.configTitle}>
                  Crie um Token de API
                </span>
                <p>
                  Na página de <strong>Opções → API → Tokens</strong>, crie um token de acesso. 
                  Este token é como uma <strong>senha especial</strong> que permite que sistemas externos 
                  acessem sua conta de forma segura.
                </p>
              </div>
            </div>
          </Col>

          <Col lg={6}>
            <div className={styles.configCard}>
              <div className={styles.configNumber}>2</div>
              <div className={styles.configIcon}>
                <Icon icon="mdi:code-tags" />
              </div>
              <div className={styles.configContent}>
                <span className={styles.configTitle}>
                  Configure seu Sistema
                </span>
                <p>
                  No seu sistema (site, aplicativo, ou ferramenta), configure para usar o token. 
                  Seu desenvolvedor ou técnico pode fazer isso usando a <strong>documentação técnica</strong> 
                  da API.
                </p>
              </div>
            </div>
          </Col>

          <Col lg={6}>
            <div className={styles.configCard}>
              <div className={styles.configNumber}>3</div>
              <div className={styles.configIcon}>
                <Icon icon="mdi:send" />
              </div>
              <div className={styles.configContent}>
                <span className={styles.configTitle}>
                  Envie Requisições
                </span>
                <p>
                  Seu sistema envia <strong>requisições</strong> para a API da Pagmus (por exemplo: 
                  "criar um produto" ou "registrar uma venda"). A API processa e retorna uma resposta.
                </p>
              </div>
            </div>
          </Col>

          <Col lg={6}>
            <div className={styles.configCard}>
              <div className={styles.configNumber}>4</div>
              <div className={styles.configIcon}>
                <Icon icon="mdi:check-circle" />
              </div>
              <div className={styles.configContent}>
                <span className={styles.configTitle}>
                  Receba Respostas
                </span>
                <p>
                  A API retorna uma <strong>resposta</strong> confirmando se a operação foi bem-sucedida 
                  ou se houve algum erro. Seu sistema pode então processar essa resposta.
                </p>
              </div>
            </div>
          </Col>
        </Row>
      </section>

      {/* O que a API pode fazer */}
      <section className={styles.section}>
        <div className={styles.sectionHeader}>
          <span className={styles.sectionTitle}>O que a API pode fazer?</span>
          <p className={styles.sectionSubtitle}>
            Funcionalidades disponíveis
          </p>
        </div>

        <Row className="g-4">
          <Col lg={6}>
            <div className={styles.configCard}>
              <div className={styles.configIcon}>
                <Icon icon="mdi:package-variant" />
              </div>
              <div className={styles.configContent}>
                <span className={styles.configTitle}>
                  Gerenciar Produtos
                </span>
                <p>
                  <strong>Criar, editar e atualizar</strong> produtos automaticamente. Configure nome, 
                  descrição, preço, planos e todas as informações do produto via API.
                </p>
              </div>
            </div>
          </Col>

          <Col lg={6}>
            <div className={styles.configCard}>
              <div className={styles.configIcon}>
                <Icon icon="mdi:cart" />
              </div>
              <div className={styles.configContent}>
                <span className={styles.configTitle}>
                  Gerenciar Vendas
                </span>
                <p>
                  <strong>Registrar vendas</strong> automaticamente quando uma compra acontece no seu 
                  sistema. Atualize status de pagamento e acompanhe o histórico completo.
                </p>
              </div>
            </div>
          </Col>

          <Col lg={6}>
            <div className={styles.configCard}>
              <div className={styles.configIcon}>
                <Icon icon="mdi:eye" />
              </div>
              <div className={styles.configContent}>
                <span className={styles.configTitle}>
                  Consultar Informações
                </span>
                <p>
                  <strong>Buscar e listar</strong> produtos, vendas, abandonos, boletos e outras informações 
                  da sua conta. Use filtros para encontrar exatamente o que precisa.
                </p>
              </div>
            </div>
          </Col>

          <Col lg={6}>
            <div className={styles.configCard}>
              <div className={styles.configIcon}>
                <Icon icon="mdi:shield-lock" />
              </div>
              <div className={styles.configContent}>
                <span className={styles.configTitle}>
                  Segurança Total
                </span>
                <p>
                  A API usa <strong>tokens seguros</strong> e permissões granulares. Você controla 
                  exatamente o que cada sistema pode fazer na sua conta.
                </p>
              </div>
            </div>
          </Col>
        </Row>
      </section>

      {/* Como Começar */}
      <section className={styles.section}>
        <div className={styles.sectionHeader}>
          <span className={styles.sectionTitle}>Como Começar?</span>
          <p className={styles.sectionSubtitle}>
            Primeiros passos para usar a API
          </p>
        </div>

        <div className={styles.infoCard}>
          <Icon icon="mdi:rocket-launch" className={styles.infoIcon} />
          <div>
            <p>
              Para começar a usar a API, você precisa:
            </p>
            <ol style={{ marginTop: '12px', marginBottom: 0, paddingLeft: '20px' }}>
              <li><strong>Criar um Token de API</strong> em <strong>Opções → API → Tokens</strong></li>
              <li><strong>Configurar permissões</strong> no token (o que ele pode fazer: ler, criar, editar)</li>
              <li><strong>Entregar o token</strong> para seu desenvolvedor ou técnico</li>
              <li><strong>Consultar a documentação</strong> técnica (disponível em <strong>Opções → API → Vendas</strong> ou <strong>Produtos</strong>)</li>
              <li><strong>Testar</strong> a integração em ambiente de desenvolvimento primeiro</li>
            </ol>
            <p style={{ marginTop: '12px', marginBottom: 0 }}>
              <strong>Importante:</strong> Se você não tem conhecimento técnico, recomendamos contratar 
              um desenvolvedor ou técnico para configurar a integração. A API é poderosa, mas requer 
              conhecimento de programação para ser usada.
            </p>
          </div>
        </div>
      </section>

      {/* Segurança */}
      <section className={styles.section}>
        <div className={styles.sectionHeader}>
          <span className={styles.sectionTitle}>Segurança e Permissões</span>
          <p className={styles.sectionSubtitle}>
            Como a API protege seus dados
          </p>
        </div>

        <Row className="g-4">
          <Col lg={6}>
            <div className={styles.participantCard}>
              <div className={`${styles.cardIcon} ${styles.iconSuccess}`}>
                <Icon icon="mdi:key" />
              </div>
              <div className={styles.cardContent}>
                <div className={`${styles.cardBadge} ${styles.badgeSuccess}`}>Tokens Seguros</div>
                <span className={styles.cardTitle}>Tokens de Acesso</span>
                <p>
                  Cada token é <strong>único e secreto</strong>. Se alguém descobrir seu token, 
                  você pode <strong>revogá-lo imediatamente</strong> e criar um novo.
                </p>
              </div>
            </div>
          </Col>

          <Col lg={6}>
            <div className={styles.participantCard}>
              <div className={`${styles.cardIcon} ${styles.iconPlatform}`}>
                <Icon icon="mdi:shield-check" />
              </div>
              <div className={styles.cardContent}>
                <div className={`${styles.cardBadge} ${styles.badgePlatform}`}>Permissões</div>
                <span className={styles.cardTitle}>Controle Total</span>
                <p>
                  Você define <strong>exatamente o que cada token pode fazer</strong>: apenas ler, 
                  criar, editar, ou tudo. Controle granular de permissões!
                </p>
              </div>
            </div>
          </Col>

          <Col lg={6}>
            <div className={styles.participantCard}>
              <div className={`${styles.cardIcon} ${styles.iconWarning}`}>
                <Icon icon="mdi:account-lock" />
              </div>
              <div className={styles.cardContent}>
                <div className={`${styles.cardBadge} ${styles.badgeWarning}`}>Isolamento</div>
                <span className={styles.cardTitle}>Apenas Seus Dados</span>
                <p>
                  Cada token só acessa <strong>seus próprios dados</strong>. Não é possível ver 
                  ou modificar informações de outros usuários.
                </p>
              </div>
            </div>
          </Col>

          <Col lg={6}>
            <div className={styles.participantCard}>
              <div className={`${styles.cardIcon} ${styles.iconAffiliate}`}>
                <Icon icon="mdi:file-document-check" />
              </div>
              <div className={styles.cardContent}>
                <div className={`${styles.cardBadge} ${styles.badgeAffiliate}`}>Auditoria</div>
                <span className={styles.cardTitle}>Histórico Completo</span>
                <p>
                  Todas as ações via API são <strong>registradas e auditadas</strong>. Você pode 
                  ver quem fez o quê e quando.
                </p>
              </div>
            </div>
          </Col>
        </Row>
      </section>

      {/* FAQ */}
      <section className={styles.section}>
        <div className={styles.sectionHeader}>
          <span className={styles.sectionTitle}>Perguntas Frequentes</span>
          <p className={styles.sectionSubtitle}>
            Tire suas dúvidas sobre a API
          </p>
        </div>

        <Row className="g-4">
          <Col lg={12}>
            <div className={styles.infoCard}>
              <Icon icon="mdi:help-circle" className={styles.infoIcon} />
              <div>
                <p style={{ marginBottom: '8px' }}>
                  <strong>Preciso saber programar para usar a API?</strong>
                </p>
                <p style={{ marginBottom: '16px' }}>
                  Sim, a API requer conhecimento de programação. Se você não sabe programar, 
                  recomendamos contratar um <strong>desenvolvedor ou técnico</strong> para configurar 
                  a integração. Você pode criar os tokens e definir permissões, mas a configuração 
                  técnica precisa ser feita por alguém com conhecimento.
                </p>
              </div>
            </div>
          </Col>

          <Col lg={12}>
            <div className={styles.infoCard}>
              <Icon icon="mdi:help-circle" className={styles.infoIcon} />
              <div>
                <p style={{ marginBottom: '8px' }}>
                  <strong>A API é segura?</strong>
                </p>
                <p style={{ marginBottom: '16px' }}>
                  Sim! A API usa <strong>tokens seguros</strong> e <strong>criptografia</strong>. 
                  Cada token tem permissões específicas e você pode revogá-lo a qualquer momento. 
                  Além disso, todas as ações são registradas para auditoria.
                </p>
              </div>
            </div>
          </Col>

          <Col lg={12}>
            <div className={styles.infoCard}>
              <Icon icon="mdi:help-circle" className={styles.infoIcon} />
              <div>
                <p style={{ marginBottom: '8px' }}>
                  <strong>Posso usar a API para integrar com meu site?</strong>
                </p>
                <p style={{ marginBottom: '16px' }}>
                  Sim! A API foi feita exatamente para isso. Você pode integrar com <strong>qualquer 
                  sistema</strong>: site, aplicativo, sistema de gestão, ou ferramenta. Basta que 
                  seu desenvolvedor configure a integração usando a documentação técnica.
                </p>
              </div>
            </div>
          </Col>

          <Col lg={12}>
            <div className={styles.infoCard}>
              <Icon icon="mdi:help-circle" className={styles.infoIcon} />
              <div>
                <p style={{ marginBottom: '8px' }}>
                  <strong>Quantos tokens posso criar?</strong>
                </p>
                <p style={{ marginBottom: '16px' }}>
                  Você pode criar <strong>quantos tokens quiser</strong>. Recomendamos criar um token 
                  diferente para cada sistema ou integração. Assim, se um token for comprometido, 
                  você pode revogá-lo sem afetar os outros.
                </p>
              </div>
            </div>
          </Col>

          <Col lg={12}>
            <div className={styles.infoCard}>
              <Icon icon="mdi:help-circle" className={styles.infoIcon} />
              <div>
                <p style={{ marginBottom: '8px' }}>
                  <strong>Onde encontro a documentação técnica?</strong>
                </p>
                <p style={{ marginBottom: '16px' }}>
                  A documentação técnica está disponível em <strong>Opções → API</strong>. Você encontrará 
                  seções específicas para <strong>Vendas</strong> e <strong>Produtos</strong>, com exemplos 
                  de código e explicações detalhadas. Também há uma interface Swagger para testar a API.
                </p>
              </div>
            </div>
          </Col>
        </Row>
      </section>
    </div>
  );
};

export default OQueEApiWiki;

